import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

// ===========================================================================
// Dos paneles con su propio scroll y un divisor que se arrastra.
//
// El problema que resuelve, dicho por quien lo usa: «cuando yo comienzo a bajar,
// el PDF se queda arriba». Con las dos columnas en el flujo normal de la página
// es el DOCUMENTO el que se desplaza, así que mirar la línea 180 deja el papel
// fuera de la pantalla — justo cuando hace falta para compararla.
//
// La zona ocupa lo que queda de ventana y cada lado se desplaza por dentro. El
// alto se MIDE, no se escribe a mano: encima hay una cabecera que cambia de
// altura —el aviso de «sin guardar» aparece y desaparece— y un `calc()` fijo
// deja la tabla cortada o con un hueco debajo según el día.
//
// El ancho lo elige quien mira y se recuerda: en un extracto del BCP se quiere
// la tabla ancha porque las descripciones son largas; en uno del BBVA se quiere
// el papel grande. Obligar a reajustarlo en cada visita es pedir el mismo gesto
// cincuenta veces al mes.
// ===========================================================================

/** Los topes del divisor, en porcentaje del ancho de la zona. */
const MINIMO = 28;
const MAXIMO = 82;
const POR_DEFECTO = 62;

/** El alto mínimo de la zona: por debajo, la tabla no enseña ni cinco filas. */
const ALTO_MINIMO = 340;

/**
 * Lee el ancho guardado. Envuelto en `try` porque `localStorage` lanza en
 * ventanas privadas y con las cookies de terceros bloqueadas, y por un ancho de
 * columna no se cae una pantalla.
 */
function anchoGuardado(clave: string): number {
  try {
    const v = Number(window.localStorage.getItem(clave));
    return Number.isFinite(v) && v >= MINIMO && v <= MAXIMO ? v : POR_DEFECTO;
  } catch {
    return POR_DEFECTO;
  }
}

export function DosPaneles({
  clave,
  izquierda,
  derecha,
  /** Sin panel derecho, la izquierda ocupa todo y no hay divisor. */
  soloIzquierda = false,
}: {
  /** Con qué nombre se recuerda el ancho. */
  clave: string;
  izquierda: React.ReactNode;
  derecha: React.ReactNode;
  soloIzquierda?: boolean;
}) {
  const zona = useRef<HTMLDivElement>(null);
  const [alto, setAlto] = useState<number | null>(null);
  const [ancho, setAncho] = useState(POR_DEFECTO);
  const [arrastrando, setArrastrando] = useState(false);

  // El ancho guardado se lee en el cliente y no en el estado inicial: en el
  // primer pintado no hay `window`, y leerlo ahí rompería el renderizado.
  useEffect(() => setAncho(anchoGuardado(clave)), [clave]);

  // El alto que queda de ventana, medido. Se vuelve a medir cuando cambia lo
  // que hay ENCIMA —que es lo que mueve el punto de partida— y al redimensionar.
  useLayoutEffect(() => {
    const medir = () => {
      const el = zona.current;
      if (!el) return;
      const arriba = el.getBoundingClientRect().top;
      setAlto(Math.max(ALTO_MINIMO, window.innerHeight - arriba - 16));
    };
    medir();
    window.addEventListener("resize", medir);
    const padre = zona.current?.parentElement;
    const vigilante = padre ? new ResizeObserver(medir) : null;
    if (padre && vigilante) vigilante.observe(padre);
    return () => {
      window.removeEventListener("resize", medir);
      vigilante?.disconnect();
    };
  }, []);

  const mover = useCallback((e: PointerEvent | React.PointerEvent) => {
    const el = zona.current;
    if (!el) return;
    const caja = el.getBoundingClientRect();
    const pct = ((e.clientX - caja.left) / caja.width) * 100;
    setAncho(Math.min(MAXIMO, Math.max(MINIMO, pct)));
  }, []);

  // El arrastre se sigue en `window` y no en el divisor: el puntero se adelanta
  // a la barra en cuanto se mueve rápido, y escuchando solo en ella el arrastre
  // se corta a mitad.
  useEffect(() => {
    if (!arrastrando) return;
    const alSoltar = () => {
      setArrastrando(false);
      try {
        window.localStorage.setItem(clave, String(Math.round(ancho)));
      } catch {
        // Sin memoria se sigue trabajando; solo se pierde el ancho elegido.
      }
    };
    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", alSoltar);
    // Mientras se arrastra no se selecciona texto de la tabla por debajo.
    const antes = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      window.removeEventListener("pointermove", mover);
      window.removeEventListener("pointerup", alSoltar);
      document.body.style.userSelect = antes;
      document.body.style.cursor = "";
    };
  }, [arrastrando, mover, ancho, clave]);

  /**
   * Mueve el divisor y lo recuerda.
   *
   * Recibe una FUNCIÓN del ancho anterior, no un número. Con un número, cinco
   * flechas seguidas parten todas del mismo valor —React agrupa los cambios— y
   * las cinco mueven lo mismo que una: se pulsa cinco veces y se avanza un paso.
   */
  const guardar = (siguiente: (previo: number) => number) => {
    setAncho((previo) => {
      const v = Math.min(MAXIMO, Math.max(MINIMO, siguiente(previo)));
      try {
        window.localStorage.setItem(clave, String(Math.round(v)));
      } catch {
        // Sin memoria se sigue trabajando; solo se pierde el ancho elegido.
      }
      return v;
    });
  };

  return (
    <div
      ref={zona}
      // El alto medido, y la plantilla de columnas por variable CSS: cambiarla
      // por variable y no reconstruyendo la clase evita que el navegador
      // recalcule los estilos en cada píxel del arrastre.
      style={{
        ...(alto ? { height: alto } : {}),
        ...(soloIzquierda
          ? {}
          : ({
              "--cols": `${ancho}% 9px minmax(0,1fr)`,
            } as React.CSSProperties)),
      }}
      className={cn(
        "grid min-h-0 gap-0",
        // En pantalla angosta no hay dos columnas que ajustar: el papel se
        // apila debajo y la zona vuelve a crecer con su contenido.
        soloIzquierda
          ? "grid-cols-1"
          : "grid-cols-1 xl:grid-cols-[var(--cols)]",
      )}
    >
      <div className="flex min-h-0 min-w-0 flex-col">{izquierda}</div>

      {soloIzquierda ? null : (
        <>
          {/* El divisor. `role="separator"` y las flechas: quien no usa ratón
              también tiene que poder repartir el ancho. */}
          {/* biome-ignore lint/a11y/useSemanticElements: un `<hr>` no se arrastra ni recibe foco, y un divisor ajustable ES un control */}
          <button
            type="button"
            role="separator"
            aria-orientation="vertical"
            aria-label="Ajustar el ancho de la tabla"
            aria-valuenow={Math.round(ancho)}
            aria-valuemin={MINIMO}
            aria-valuemax={MAXIMO}
            onPointerDown={(e) => {
              e.preventDefault();
              setArrastrando(true);
            }}
            onDoubleClick={() => guardar(() => POR_DEFECTO)}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                guardar((v) => v - 2);
              } else if (e.key === "ArrowRight") {
                e.preventDefault();
                guardar((v) => v + 2);
              } else if (e.key === "Home") {
                e.preventDefault();
                guardar(() => POR_DEFECTO);
              }
            }}
            title="Arrastrá para repartir el ancho · doble clic para volver al reparto de fábrica"
            className={cn(
              "group hidden cursor-col-resize items-center justify-center xl:flex",
              "focus-visible:outline-none",
            )}
          >
            <span
              className={cn(
                "h-full w-[3px] rounded-full transition-colors",
                arrastrando
                  ? "bg-cyan-500"
                  : "bg-border group-hover:bg-cyan-500/60 group-focus-visible:bg-cyan-500",
              )}
            />
          </button>

          <div className="mt-3.5 flex min-h-0 min-w-0 flex-col xl:mt-0">
            {derecha}
          </div>
        </>
      )}
    </div>
  );
}
